import React from "react";
import { BUCKET_OF, READY_GREEN, ASSUM_ORANGE, BLOCKED_RED, REVIEW_PURPLE } from "./methodologyUtils";
import { stateLabel, stateColor } from "./labels";

// Small pill/badge used across the Data Seamless tables. Light-theme adaptation
// of the coloured `.badge-*` spans from the standalone test UI: each badge shows
// its label with a tinted background derived from a single accent colour.

const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Accent colour per gap status (matches the test UI palette).
const GAP_STATUS_COLORS = {
  MISSING: "#d97706",
  BLOCKING: "#dc2626",
  ASSUMPTION_PENDING: "#d97706",
  ASSUMPTION_REJECTED: "#dc2626",
  REQUESTED: "#2563eb",
  RESOLVED_PROVIDED: "#059669",
  RESOLVED_ASSUMPTION: "#059669",
  INVALID_VALUE: "#9333ea",
  EXPIRED: "#64748b",
  UNRESOLVED_MAPPING: "#ea580c",
};

const READINESS_COLORS = {
  READY: "#059669",
  WITH_ASSUMPTIONS: "#d97706",
  INCOMPLETE: "#d97706",
  BLOCKED: "#dc2626",
  PARTIAL: "#d97706",
  NOT_READY: "#dc2626",
};

const LEVEL_COLORS = {
  L1: "#4f46e5",
  L2: "#db2777",
};

// LCA requirement: M (mandatory) / M-A (mandatory, assumable) / R (recommended).
const REQ_COLORS = {
  M: "#dc2626",
  "M-A": "#d97706",
  R: "#4f46e5",
};

const baseStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
  lineHeight: 1.5,
};

export const DsBadge = ({ label, color = "#64748b" }) => (
  <span
    style={{
      ...baseStyle,
      color,
      backgroundColor: hexToRgba(color, 0.14),
    }}
  >
    {label}
  </span>
);

export const GapStatusBadge = ({ status }) => (
  <DsBadge label={status || "—"} color={GAP_STATUS_COLORS[status]} />
);

export const ReadinessBadge = ({ status }) =>
  status ? <DsBadge label={status} color={READINESS_COLORS[status]} /> : <span>—</span>;

export const LevelBadge = ({ level }) =>
  level ? <DsBadge label={level} color={LEVEL_COLORS[level]} /> : <span>—</span>;

// Backend sends requiredForLCA as one of M / M-A / R (or null when N/A).
export const ReqBadge = ({ req }) =>
  req ? <DsBadge label={req} color={REQ_COLORS[req]} /> : <span style={{ color: "#94a3b8" }}>—</span>;

// Gap status → user-facing bucket word badge (Falta / Asumible / Asumido / Aportado).
// Single source of the status → word+colour mapping, shared by the ficha and the entity panels.
const BUCKET_LABEL = { faltan: "Falta", revisar: "Revisar", asumibles: "Asumible", asumidos: "Asumido", aportados: "Aportado" };
const BUCKET_COLOR = { faltan: BLOCKED_RED, revisar: REVIEW_PURPLE, asumibles: ASSUM_ORANGE, asumidos: ASSUM_ORANGE, aportados: READY_GREEN };
export const GapChip = ({ status }) => {
  const b = BUCKET_OF[status] || "faltan";
  return <DsBadge label={BUCKET_LABEL[b]} color={BUCKET_COLOR[b]} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// Redesign badges (spec §3/§8). StateBadge shows the *state* of the datum with a
// severity-driven colour (see labels.js); ImpactText shows the *methodological impact*
// as a separate line. The two are intentionally distinct so the UI never conflates
// "estado del dato" with "impacto metodológico".
// ─────────────────────────────────────────────────────────────────────────────

// State of a datum: "Falta" / "Puedes asumir" / "Asumido" / … with severity colour.
export const StateBadge = ({ status }) =>
  status ? <DsBadge label={stateLabel(status)} color={stateColor(status)} /> : <span>—</span>;

// Methodological impact line, e.g. "Bloquea LCA · EcoScore". Rendered as text (not a pill)
// to match the spec's stacked layout; red when it blocks, muted otherwise. `label` is the
// pre-built string from labels.impactLabel(); nothing renders when it is empty.
export const ImpactText = ({ label, blocking = true }) =>
  label ? (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: blocking ? BLOCKED_RED : "#64748b",
      }}
    >
      {label}
    </span>
  ) : null;
